<script setup lang="ts">
import BaseLayoutLicenseWarning from '@/components/layouts/BaseLayout/BaseLayoutLicenseWarning.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { useReactiveFilteredNavigation } from '@/navigation';
import { mainNavigationRaw } from '@/navigation/menus/mainNavigationRaw';
import { useAuthenticationStore } from '@/stores/authenticationStore';
import { useLicenseStore } from '@/stores/licenseStore.ts';
import { I18nKey } from '@/utils/typings.ts';
import { storeToRefs } from 'pinia';
import BaseLayoutHeaderUserMenu from './BaseLayoutHeaderUserMenu.vue';

const navigation = useReactiveFilteredNavigation(mainNavigationRaw);
const { isAuthenticated } = storeToRefs(useAuthenticationStore());

const licenseStore = useLicenseStore();
const { licenseHasIssues } = storeToRefs(licenseStore);
</script>

<template>
  <v-app-bar class="headerBar" height="64" :extensionHeight="32">
    <v-app-bar-title class="logo-container">
      <div class="logo text-body-2 font-weight-bold">{{ $t('Unicheck') }}</div>
    </v-app-bar-title>

    <template v-if="isAuthenticated" #prepend>
      <v-menu theme="dark" class="d-md-none" transition="fade-transition">
        <template #activator="{ props }">
          <v-app-bar-nav-icon class="d-md-none" v-bind="props" />
        </template>
        <v-list>
          <v-list-item
            v-for="entry in navigation.entries"
            :key="entry.titleI18n"
            :to="entry.route!"
            :value="entry"
            color="primary"
          >
            <template #prepend>
              <VIcon :icon="entry.icon" />
            </template>

            <v-list-item-title> {{ $t(entry.tooltipI18n as I18nKey) }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>

      <div class="leftNavigation d-md-block d-none">
        <span v-for="entry in navigation.entries" :key="entry.titleI18n" class="entry">
          <Tooltip :disabled="!entry.tooltipI18n" position="bottom" :showPointer="true">
            <template #target="{ props }">
              <PureButton
                v-bind="props"
                :to="entry.route!"
                class="link"
                :aria-label="entry.titleI18n"
              >
                <v-icon size="32">{{ entry.icon }}</v-icon>
              </PureButton>
            </template>
            <template v-if="entry.tooltipI18n" #tooltip>
              {{ $t(entry.tooltipI18n as I18nKey) }}
            </template>
          </Tooltip>
        </span>
      </div>
    </template>

    <template v-if="isAuthenticated" #append>
      <div class="rightNavigation">
        <BaseLayoutHeaderUserMenu />
      </div>
    </template>

    <template v-if="isAuthenticated && licenseHasIssues" #extension>
      <BaseLayoutLicenseWarning />
    </template>
  </v-app-bar>
</template>

<style scoped lang="scss">
@use '@/styles/variables';

.headerBar {
  background: rgb(var(--v-theme-secondary-darken-2)) !important;
  display: flex;
  justify-content: space-between;
  color: #fff;
  align-items: center;

  :deep(.v-toolbar__content) {
    padding: 0 variables.$horizontalAppPadding;
  }

  .leftNavigation {
    height: 36px;

    .entry {
      margin-right: 24px;
      display: inline-block;
      &:last-child {
        margin-right: 12px;
      }
    }
  }
  .leftNavigation,
  .rightNavigation {
    z-index: 1;
  }
  .rightNavigation {
    align-items: center;
    display: flex;
  }
}
.logo-container {
  position: absolute;
  height: 100%;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  margin-inline-start: 0;

  .logo {
    font-size: 24px !important;
    pointer-events: auto;
  }
}
</style>
